import { useCallback, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Camera, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { ReactNode } from 'react'

import type { Product } from '@/lib/api/product-api'
import type { LotPickerSource } from '@/components/lots/lot-picker-source'
import { MobileBarcodeScannerDialog } from '@/components/orders/mobile-barcode-scanner-dialog'
import { Button } from '@/components/ui/button'
import { LotPicker } from '@/components/lots/lot-picker'
import { FieldError } from '@/components/ui/field'
import { parseLotDiscountInput } from '@/lib/lot-discount'
import { PositionPicker } from '@/components/warehouse-positions/position-picker'
import { ProductPicker } from '@/components/products/product-picker'
import { Input } from '@/components/ui/input'
import { QuantityInput } from '@/components/ui/quantity-input'
import { Label } from '@/components/ui/label'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// ProductLotItemsField — multi-lot picker cho cả PNK + PXK lô sản phẩm.
//
// mode='in': mỗi row có toggle "Lô mới" / "Lô có sẵn"
// mode='out': chỉ pick lô có sẵn
//
// CRITICAL: dùng useFieldArray.update(idx, newValue) khi toggle mode row —
// KHÔNG dùng setValue(path, val) (anti-pattern gây orphan field cũ trong store).

// today() returns local-date `YYYY-MM-DD` (not toISOString — that shifts to UTC).
function today(): string {
    const d = new Date()
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${mo}-${dd}`
}

// addOneYear returns YYYY-MM-DD 1 năm sau `iso`. Bảo toàn ngày (leap-year adjust
// nếu vào 29/2).
function addOneYear(iso: string): string {
    if (!iso) return ''
    const [ys, ms, ds] = iso.split('-')
    const y = Number(ys)
    const mo = Number(ms)
    const d = Number(ds)
    if (!y || !mo || !d) return ''
    const dt = new Date(y + 1, mo - 1, d)
    // Nếu 29/2 → 28/2 năm sau (JS auto rollover to 1/3, cần adjust).
    if (dt.getMonth() !== mo - 1) dt.setDate(0)
    const ny = dt.getFullYear()
    const nm = String(dt.getMonth() + 1).padStart(2, '0')
    const nd = String(dt.getDate()).padStart(2, '0')
    return `${ny}-${nm}-${nd}`
}

export interface WarehouseOption {
    value: string
    label: string
}

/** render tuỳ chọn cho dropdown row (Combobox); trigger vẫn dùng `label`. */
export interface ProductLotOption {
    render?: () => ReactNode
    value: string
    label: string
    available?: number
    unit?: string
    /** Chữ hiện trên NÚT sau khi chọn, khi nó cần khác `label` — xem
     *  ComboboxOption.triggerLabel. */
    triggerLabel?: string
    /** SP của lô — xem LotOption.productId. */
    productId?: string
    /** CK đánh dấu của lô — xem LotOption.discountPercent. */
    discountPercent?: number
}

interface RowNew {
    kind: 'new'
    new_lot_code?: string
    new_lot_product_id: string
    new_lot_warehouse_id?: string
    new_lot_position_id?: string
    new_lot_manufactured_date?: string
    new_lot_expiry_date?: string
    /** Mức CK đánh dấu cho lô (0 = không đánh dấu). Ưu tiên chọn lô khi đơn CK khớp. */
    new_lot_discount_percent?: number
    quantity: number
}

interface RowExisting {
    kind: 'existing'
    product_lot_id: string
    quantity: number
}

export type ProductLotItemRow = RowNew | RowExisting

interface Props {
    mode: 'in' | 'out'
    lotOptions?: ProductLotOption[]
    /** Nguồn lô dùng chung (`useProductLotSource`): options + tìm server + cuộn
     *  tải thêm + tra lô đã rơi khỏi trang. Khi truyền thì thay cho lotOptions /
     *  onLotSearchChange. */
    lotSource?: LotPickerSource
    /** Server-side search callback cho lot Combobox (mode='out'). Caller cần debounce. */
    onLotSearchChange?: (search: string) => void
    fieldName?: string
    maxItems?: number
    /** Extra buttons render bên trái nút "Thêm lô" trên header (vd: nút Quét mã lô). */
    headerAction?: ReactNode
    /** Render thêm UI dưới mỗi row (vd: picker lô lấy hương nguồn cho from_packaging). */
    renderExtraPerItem?: (idx: number) => ReactNode
    /** Extra lot options inject vào Combobox (mode='out') — dùng cho lot
     *  quét mã vạch mà nằm ngoài top N của useProductLotsQuery. Parent fetch
     *  lot by ID sau scan rồi truyền vào đây. Merge với lotOptions
     *  (scannedLots wins nếu trùng id). */
    scannedLots?: ProductLotOption[]
}

const NO_LOTS: ProductLotOption[] = []

export function ProductLotItemsField({
    mode,
    lotOptions: lotOptionsProp,
    lotSource,
    onLotSearchChange: onLotSearchChangeProp,
    fieldName = 'items',
    maxItems,
    headerAction,
    scannedLots,
    renderExtraPerItem,
}: Props) {
    const lotOptions = lotSource?.options ?? lotOptionsProp ?? NO_LOTS
    const onLotSearchChange = lotSource?.onSearchChange ?? onLotSearchChangeProp
    const [scannerRowIndex, setScannerRowIndex] = useState<number | null>(null)
    // SP quét barcode có thể nằm ngoài trang ProductPicker đang tải → giữ lại để
    // truyền `extraProducts`. SP chọn tay thì ProductPicker tự nhớ nhãn.
    const [scannedProducts, setScannedProducts] = useState<
        Map<string, Product>
    >(new Map())
    const scannedProductList = useMemo(
        () => Array.from(scannedProducts.values()),
        [scannedProducts],
    )
    // Cùng vấn đề cho ô chọn LÔ khi form dùng server-search lô (mode='out' PXK):
    // `scannedLots` prop chỉ giữ lô quét barcode. Lô chọn tay cần cache local,
    // nếu không nó biến mất khỏi trigger khi lotOptions đổi theo search dùng chung.
    const [pickedLots, setPickedLots] = useState<Map<string, ProductLotOption>>(
        new Map(),
    )
    const cacheLot = useCallback((opt: ProductLotOption) => {
        setPickedLots((prev) => {
            if (prev.has(opt.value)) return prev
            const next = new Map(prev)
            next.set(opt.value, opt)
            return next
        })
    }, [])
    const { control, register, watch, setValue } = useFormContext()
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: fieldName,
    })
    const scannerOpen = scannerRowIndex !== null
    // Đọc toàn bộ items để loại product_lot_id đã chọn ở row khác — tránh
    // user chọn cùng 1 lô 2 lần (approve sẽ 500 vì deduct vượt available).
    const allItems = watch(fieldName) as
        Array<Record<string, unknown>> | undefined
    const selectedLotIds = useMemo(
        () =>
            (allItems ?? []).map(
                (row) => (row?.product_lot_id as string | undefined) ?? '',
            ),
        [allItems],
    )

    // Merge lotOptions từ server + scannedLots (quét mã ở PXK) + pickedLots (chọn
    // tay). Cả 2 cache đều "wins" để lô đã dùng không rớt khỏi list khi search đổi.
    const mergedLotOptions = useMemo<ProductLotOption[]>(() => {
        if (
            (!scannedLots || scannedLots.length === 0) &&
            pickedLots.size === 0
        ) {
            return lotOptions
        }
        const map = new Map<string, ProductLotOption>()
        for (const opt of lotOptions) map.set(opt.value, opt)
        for (const opt of pickedLots.values()) map.set(opt.value, opt)
        for (const opt of scannedLots ?? []) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [lotOptions, scannedLots, pickedLots])
    // Lô đang chọn có thể đã rơi khỏi trang hiện tại (đổi từ khoá) — lotSource
    // vẫn nhớ nó, nên số dư / đơn vị / nhãn không mất.
    const findLot = (id: unknown) =>
        mergedLotOptions.find((o) => o.value === id) ??
        lotSource?.getOption(typeof id === 'string' ? id : undefined)
    // Ô chọn lô dùng LotPicker chung: nguồn = lô từ server/caller + lô quét mã
    // + lô đã chọn tay, tra thêm qua lotSource cho lô đã rơi khỏi trang.
    const pickerSource: LotPickerSource = {
        options: mergedLotOptions,
        getOption: findLot,
        onSearchChange: onLotSearchChange ?? (() => undefined),
        onLoadMore: lotSource?.onLoadMore,
        hasMore: lotSource?.hasMore,
        loadingMore: lotSource?.loadingMore,
    }

    const addRow = () => {
        if (mode === 'in') {
            const nsx = today()
            append({
                kind: 'new' as const,
                new_lot_product_id: '',
                // '' chứ không để undefined — xem chú thích ở stock-in-notes/new.tsx:
                // undefined làm z.string().uuid() abort item → superRefine bị bỏ qua.
                new_lot_position_id: '',
                new_lot_manufactured_date: nsx,
                new_lot_expiry_date: addOneYear(nsx),
                quantity: 0,
            } satisfies RowNew)
        } else {
            append({
                kind: 'existing' as const,
                product_lot_id: '',
                quantity: 0,
            } satisfies RowExisting)
        }
    }

    const handleScannerOpenChange = useCallback((open: boolean) => {
        if (!open) setScannerRowIndex(null)
    }, [])

    const handleProductScanned = useCallback(
        (product: Product) => {
            if (scannerRowIndex === null) return
            // Giữ SP vừa quét trong options của ProductPicker (nó có thể nằm
            // ngoài trang đang tải).
            setScannedProducts((prev) => {
                if (prev.has(product.id)) return prev
                const next = new Map(prev)
                next.set(product.id, product)
                return next
            })
            setValue(
                `${fieldName}.${scannerRowIndex}.new_lot_product_id`,
                product.id,
                { shouldDirty: true, shouldValidate: true },
            )
            // Autofill unit so the "Đơn vị" hint below the picker matches the
            // scanned product without a manual re-click.
            setValue(
                `${fieldName}.${scannerRowIndex}.new_lot_product_unit`,
                product.unit,
                { shouldDirty: true },
            )
            setScannerRowIndex(null)
        },
        [fieldName, scannerRowIndex, setValue],
    )

    // Không tìm thấy sản phẩm khớp mã vạch: báo rõ để thủ kho biết mã chưa
    // gán (nhiều SP chưa có barcode) và chọn tay, thay vì đóng dialog im lặng.
    const handleBarcodeNotFound = useCallback((barcode: string) => {
        toast.error(m.pos_barcode_not_found_desc({ barcode }))
    }, [])

    // Thanh nút "Thêm lô" + "Quét mã" đặt DƯỚI danh sách item: user thêm rồi sửa
    // ngay tại chỗ, không phải kéo lên đầu form mỗi lần muốn thêm dòng.
    const actionBar = (
        <div className="flex items-center gap-2">
            {headerAction}
            {(maxItems === undefined || fields.length < maxItems) && (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addRow}
                >
                    <Plus className="size-4 mr-1" />
                    {m.lot_items_add()}
                </Button>
            )}
        </div>
    )

    return (
        <div className="space-y-3">
            <Label>{m.lot_items_title()}</Label>

            {fields.length === 0 && (
                <div className="rounded-md border border-dashed bg-muted/30 py-6 text-center text-sm text-muted-foreground">
                    {m.lot_items_empty()}
                </div>
            )}

            {fields.map((field, idx) => {
                const kind = watch(`${fieldName}.${idx}.kind`) as
                    'new' | 'existing'
                return (
                    <div
                        key={field.id}
                        className="grid gap-3 rounded-md border bg-card p-3 min-w-0"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                                {m.lot_items_row({ n: idx + 1 })}
                            </span>
                            <div className="flex items-center gap-2">
                                {mode === 'in' && (
                                    <div className="flex rounded-md border p-0.5 text-sm md:text-xs">
                                        <button
                                            type="button"
                                            className={`min-h-11 rounded px-3 py-2 md:min-h-0 md:px-2 md:py-1 ${kind === 'new' ? 'bg-primary text-primary-foreground' : ''}`}
                                            onClick={() =>
                                                update(idx, {
                                                    kind: 'new' as const,
                                                    new_lot_product_id: '',
                                                    // '' để superRefine nguồn
                                                    // vẫn chạy (xem new.tsx).
                                                    new_lot_position_id: '',
                                                    quantity: 0,
                                                } satisfies RowNew)
                                            }
                                        >
                                            {m.lot_items_new_lot()}
                                        </button>
                                        <button
                                            type="button"
                                            className={`min-h-11 rounded px-3 py-2 md:min-h-0 md:px-2 md:py-1 ${kind === 'existing' ? 'bg-primary text-primary-foreground' : ''}`}
                                            onClick={() =>
                                                update(idx, {
                                                    kind: 'existing' as const,
                                                    product_lot_id: '',
                                                    quantity: 0,
                                                } satisfies RowExisting)
                                            }
                                        >
                                            {m.lot_items_existing_lot()}
                                        </button>
                                    </div>
                                )}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => remove(idx)}
                                >
                                    <Trash2 className="size-4 text-destructive" />
                                </Button>
                            </div>
                        </div>

                        {mode === 'in' && kind === 'new' && (
                            <div className="grid gap-3 md:grid-cols-3">
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_code_label()}
                                    </Label>
                                    <Input
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_code`,
                                        )}
                                        placeholder="SP-LOT-..."
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_code`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.product_lot_new_lot_product_label()}
                                    </Label>
                                    <div className="flex gap-2">
                                        <ProductPicker
                                            className="min-w-0 flex-1"
                                            value={watch(
                                                `${fieldName}.${idx}.new_lot_product_id`,
                                            )}
                                            extraProducts={scannedProductList}
                                            onChange={(v, product) => {
                                                setValue(
                                                    `${fieldName}.${idx}.new_lot_product_id`,
                                                    v,
                                                )
                                                // Autofill unit from the picked
                                                // product so downstream rows /
                                                // reports don't need a lookup.
                                                setValue(
                                                    `${fieldName}.${idx}.new_lot_product_unit`,
                                                    product?.unit ?? '',
                                                )
                                            }}
                                            placeholder={m.product_lot_new_lot_product_label()}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            className="shrink-0"
                                            onClick={() =>
                                                setScannerRowIndex(idx)
                                            }
                                            aria-label={m.mobile_scan_barcode_button()}
                                            title={m.mobile_scan_barcode_button()}
                                        >
                                            <Camera className="size-4" />
                                        </Button>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {m.order_unit_col()}:{' '}
                                        {watch(
                                            `${fieldName}.${idx}.new_lot_product_unit`,
                                        ) ||
                                            (watch(
                                                `${fieldName}.${idx}.new_lot_product_id`,
                                            )
                                                ? '—'
                                                : '—')}
                                    </p>
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_product_id`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.lot_position_label()}
                                    </Label>
                                    <PositionPicker
                                        lotType="product"
                                        value={watch(
                                            `${fieldName}.${idx}.new_lot_position_id`,
                                        )}
                                        onChange={(v) =>
                                            setValue(
                                                `${fieldName}.${idx}.new_lot_position_id`,
                                                v,
                                            )
                                        }
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_position_id`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_manufactured_label()}
                                    </Label>
                                    <Input
                                        type="date"
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_manufactured_date`,
                                            {
                                                onChange: (e) => {
                                                    // Auto-nhảy HSD = NSX +
                                                    // 1 năm khi user đổi NSX.
                                                    // User vẫn có thể sửa
                                                    // HSD sau — sync chỉ chạy
                                                    // tại thời điểm sửa NSX.
                                                    const nsx = e.target.value
                                                    setValue(
                                                        `${fieldName}.${idx}.new_lot_expiry_date`,
                                                        nsx
                                                            ? addOneYear(nsx)
                                                            : '',
                                                        {
                                                            shouldValidate: true,
                                                            shouldDirty: true,
                                                        },
                                                    )
                                                },
                                            },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_manufactured_date`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_expiry_label()}
                                    </Label>
                                    <Input
                                        type="date"
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_expiry_date`,
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_expiry_date`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_quantity_label()}
                                    </Label>
                                    <QuantityInput
                                        unit={
                                            watch(
                                                `${fieldName}.${idx}.new_lot_product_unit`,
                                            ) || undefined
                                        }
                                        type="number"
                                        step="0.001"
                                        min={0}
                                        {...register(
                                            `${fieldName}.${idx}.quantity`,
                                            { valueAsNumber: true },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.quantity`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.product_lot_discount_label()}
                                    </Label>
                                    {/* Tuỳ chọn: bỏ trống = lô không đánh dấu.
                                        setValueAs thay cho valueAsNumber vì
                                        valueAsNumber biến ô rỗng thành NaN,
                                        làm zod chặn submit dù field optional.
                                        step="any" để gõ thập phân tự do; chốt
                                        làm tròn 2 số ở bước gửi API. */}
                                    <Input
                                        type="number"
                                        step="any"
                                        min={0}
                                        max={100}
                                        placeholder="0"
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_discount_percent`,
                                            {
                                                setValueAs:
                                                    parseLotDiscountInput,
                                            },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_discount_percent`}
                                    />
                                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                                        {m.product_lot_discount_hint()}
                                    </p>
                                </div>
                            </div>
                        )}

                        {(mode === 'out' ||
                            (mode === 'in' && kind === 'existing')) && (
                            <div className="grid gap-3 md:grid-cols-2">
                                <div className="min-w-0">
                                    <Label className="text-xs">
                                        {m.lot_items_select_lot()}
                                    </Label>
                                    <LotPicker
                                        source={pickerSource}
                                        clientFilter={!onLotSearchChange}
                                        excludeIds={selectedLotIds.filter(
                                            (_, i) => i !== idx,
                                        )}
                                        value={watch(
                                            `${fieldName}.${idx}.product_lot_id`,
                                        )}
                                        onChange={(v, opt) => {
                                            setValue(
                                                `${fieldName}.${idx}.product_lot_id`,
                                                v,
                                            )
                                            if (opt) cacheLot(opt)
                                        }}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.product_lot_id`}
                                    />
                                    {mode === 'out' && (
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {(() => {
                                                const lotId = watch(
                                                    `${fieldName}.${idx}.product_lot_id`,
                                                )
                                                const lot = findLot(lotId)
                                                if (
                                                    lot &&
                                                    typeof lot.available ===
                                                        'number'
                                                ) {
                                                    return m.lot_items_available_hint(
                                                        {
                                                            qty: formatQty(
                                                                lot.available,
                                                            ),
                                                            unit:
                                                                lot.unit ?? '',
                                                        },
                                                    )
                                                }
                                                return null
                                            })()}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_quantity_label()}
                                    </Label>
                                    <QuantityInput
                                        unit={
                                            findLot(
                                                watch(
                                                    `${fieldName}.${idx}.product_lot_id`,
                                                ),
                                            )?.unit || undefined
                                        }
                                        type="number"
                                        step="0.001"
                                        min={0}
                                        {...register(
                                            `${fieldName}.${idx}.quantity`,
                                            { valueAsNumber: true },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.quantity`}
                                    />
                                </div>
                            </div>
                        )}

                        {renderExtraPerItem?.(idx)}
                    </div>
                )
            })}

            {actionBar}

            <MobileBarcodeScannerDialog
                open={scannerOpen}
                onOpenChange={handleScannerOpenChange}
                onProductFound={handleProductScanned}
                onBarcodeNotFound={handleBarcodeNotFound}
            />
        </div>
    )
}
